
import { StockLocationConfig } from "@/models/dossier-configs/stock-location";
import { Product } from "@/models/base";
import { ProductStock } from "@/models/base";
import { ProductWriteOffRow } from "@/models/base";
import { SizeTable } from "@/models/base";
import { ProductSizeBarcode } from "@/models/base";
import { DossierConfigService } from "@/services/dossier-config-service";
import { SizeTableService } from "@/services/size-table-service";
import { AuthService } from "@/services";
import { MODULE } from "@/config/modules";
import Vue from "vue";
import { ISelectItem } from "winfakt-vue-components";
import AppActualStock from "./ProductActualStock.vue";
import AppProductsProductEditWriteOff from "./ProductEditWriteOff.vue";

export default Vue.extend({
	data() {
		return {
			toEditWriteOff: undefined as undefined | ProductWriteOffRow,
			advancedStockHandling: true,
			sizeTables: [] as SizeTable[],
		};
	},
	props: {
		product: {
			required: true,
			type: Object as () => Product,
		}
	},
	computed: {
		productStock():ProductStock{
			if (!this.product.Stock){
				return new ProductStock();
			}
			return this.product.Stock;
		},
		actualStock():any {
			if (!this.productStock) {
				return;
			}
			return this.stockLocationConfig.getSortedLocationNames().map((l: string, i: number) => {
				return {
					text: l,
					value: i + 1,
					stock: this.productStock["Location" + (i + 1) + "Qty" as keyof ProductStock] as number
				} as any;
			});
		},
		stockLocationConfig():StockLocationConfig {
			return DossierConfigService.getStockLocationConfig();
		},
		hasSizeModule():boolean {
			return AuthService.hasModule(MODULE.MATENTABEL);
		},
		sizeTableItems():ISelectItem<number | null>[] {
			let items:ISelectItem<number | null>[] = [
				{text: this.$ct("no-size-table").toString(), value: null}
			];
			for (let table of this.sizeTables) {
				// Keep an inactive table visible if it's the one currently linked.
				if (table.Active || table.ID === this.product.SizeTableID) {
					items.push({text: table.Name, value: table.ID});
				}
			}
			return items;
		},
		selectedSizeTable():SizeTable | undefined {
			return this.sizeTables.find(t => t.ID === this.product.SizeTableID);
		}
	},
	watch: {
		// Rebuild the barcode rows when the linked size table changes.
		"product.SizeTableID"() {
			this.syncSizeBarcodes();
		}
	},
	methods: {
		async loadSizeTables() {
			if (!this.hasSizeModule) return;
			this.sizeTables = await SizeTableService.getAll();
		},
		// Loads any existing per-size barcodes, then aligns product.SizeBarcodes with the
		// entries of the linked size table (one editable row per size).
		async loadSizeBarcodes() {
			if (!this.hasSizeModule || !this.product.SizeTableID) {
				this.product.SizeBarcodes = [];
				return;
			}
			if (this.product.ID) {
				this.product.SizeBarcodes = await SizeTableService.getProductSizeBarcodes(this.product.ID);
			}
			this.syncSizeBarcodes();
		},
		// Ensures product.SizeBarcodes has exactly one row per size of the linked table,
		// preserving already-entered barcodes and dropping rows of unlinked tables.
		syncSizeBarcodes() {
			let table = this.selectedSizeTable;
			if (!table) {
				this.product.SizeBarcodes = [];
				return;
			}
			let existing = new Map<number, ProductSizeBarcode>();
			for (let b of this.product.SizeBarcodes) {
				existing.set(b.SizeTableEntryID, b);
			}
			let rows:ProductSizeBarcode[] = [];
			for (let entry of table.getSortedEntries()) {
				let row = existing.get(entry.ID);
				if (!row) {
					row = new ProductSizeBarcode();
					row.ProductID = this.product.ID;
					row.SizeTableEntryID = entry.ID;
					row.Barcode = "";
				}
				rows.push(row);
			}
			this.product.SizeBarcodes = rows;
		},
		entryName(entryId:number):string {
			let entry = this.selectedSizeTable?.Entries.find(e => e.ID === entryId);
			return entry ? entry.Name : "?";
		},
		async generateForRow(barcode:ProductSizeBarcode) {
			barcode.Barcode = await SizeTableService.generateBarcode();
		},
		add() {
			this.toEditWriteOff = new ProductWriteOffRow();
		},
		onConfirmAdd(writeOffProduct: ProductWriteOffRow) {
			writeOffProduct.ProductID = this.product.ID;
			if (this.product.WriteOffProducts.indexOf(writeOffProduct) == -1) {
				this.product.WriteOffProducts.push(writeOffProduct);
			}
		},
		closeModal() {
			this.toEditWriteOff = undefined;
		},
		editEntry(writeOffProduct: ProductWriteOffRow) {
			this.toEditWriteOff = writeOffProduct;
		},
		removeEntry(index: number) {
			this.product.WriteOffProducts.splice(index, 1);
		}
	},
	async mounted() {
		await this.loadSizeTables();
		await this.loadSizeBarcodes();
	},
	components: {
		AppActualStock,
		AppProductsProductEditWriteOff
	},
	i18nEx: {
		componentPrefix: "components.products"
	}
});
